import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

export const KEY_LEVER_PRIORITIES = ['Primary', 'Secondary', 'Tertiary', 'Conditional'] as const;

export type KeyLeverPriority = (typeof KEY_LEVER_PRIORITIES)[number];

const GRADE_LABELS = appStrings.common.gradeNames;

export const KEY_LEVER_GRADES = [
  { key: 'g9', label: GRADE_LABELS.grade9 },
  { key: 'g10', label: GRADE_LABELS.grade10 },
  { key: 'g11', label: GRADE_LABELS.grade11 },
  { key: 'g12', label: GRADE_LABELS.grade12 },
  { key: 'c1', label: GRADE_LABELS.postSecondaryY1 },
  { key: 'c2', label: GRADE_LABELS.postSecondaryY2 },
  { key: 'c3', label: GRADE_LABELS.postSecondaryY3 },
  { key: 'c4', label: GRADE_LABELS.postSecondaryY4 },
] as const;

export type KeyLeverGradeKey = (typeof KEY_LEVER_GRADES)[number]['key'];

export const KEY_LEVER_UNLOCKS = [
  { key: 'academics', title: 'Academic Foundations' },
  { key: 'achievement', title: 'Real World Experience & Achievement' },
  { key: 'skills', title: 'Mindsets & Skills' },
  { key: 'communities', title: 'Communities of Success' },
] as const;

export type KeyLeverUnlockKey = (typeof KEY_LEVER_UNLOCKS)[number]['key'];

export type KeyLeverLinks = Readonly<{
  guide?: string;
  competency?: string;
  tools?: readonly string[];
  other?: string;
}>;

export type KeyLever = Readonly<{
  id: string;
  unlock: KeyLeverUnlockKey;
  name: string;
  directive: string;
  rationale: string;
  /** Grades a lever doesn't apply to hold `N/A` or an empty string. */
  priority: Readonly<Record<KeyLeverGradeKey, KeyLeverPriority | 'N/A' | ''>>;
  links: KeyLeverLinks;
}>;

export const keyLeversStrings = {
  title: 'Coaching key levers',
  bannerAlt: 'Coaching key levers',
  subtitle: 'Backrs Institute · Choose the right lever at the right time',
  gradeLabel: 'Student grade',
  whyThisMatters: 'Why this matters',
  otherLink: 'Link',
} as const;

export const KEY_LEVERS: readonly KeyLever[] = [
  {
    id: 'improve-gpa',
    unlock: 'academics',
    name: 'Improve GPA',
    directive:
      'Decide which 1–2 classes most impact your GPA and execute a plan to raise performance this term.',
    rationale:
      'GPA is the strongest long-term signal of academic readiness, consistency, and sustained effort.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Secondary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Secondary',
      c4: 'Tertiary',
    },
    links: { guide: 'High School GPA Guide', competency: 'Learning', tools: ['Are You On Track'] },
  },
  {
    id: 'course-rigor',
    unlock: 'academics',
    name: 'Increase Rigor & Alignment of Courses',
    directive:
      'Plan a course sequence that clearly demonstrates academic rigor aligned to your goals.',
    rationale:
      'Challenging coursework signals readiness for college-level work and predicts higher persistence and completion.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Secondary',
    },
    links: { guide: 'High School Course Selection', competency: 'Classes, Majors, Degrees' },
  },
  {
    id: 'test-scores',
    unlock: 'academics',
    name: 'Strong ACT / SAT / AP Scores',
    directive:
      'Commit to a test strategy and systematically improve the ACT or SAT sections with highest score upside.',
    rationale:
      'Literacy and numeracy proficiency strongly predict long-term earnings, employability, and life outcomes.',
    priority: {
      g9: 'Conditional',
      g10: 'Secondary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'N/A',
      c2: 'N/A',
      c3: 'N/A',
      c4: 'N/A',
    },
    links: {
      guide: 'High School Test Scores Guide',
      competency: 'Test Prep',
      tools: ['Improve Your Test Scores'],
    },
  },
  {
    id: 'college-application',
    unlock: 'academics',
    name: 'College Application Strategy',
    directive: 'Build and defend a college list that balances fit, outcomes, and affordability.',
    rationale:
      'A clear application strategy reduces mismatches and increases persistence, completion, and post-college success.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Secondary',
      g12: 'Primary',
      c1: 'N/A',
      c2: 'N/A',
      c3: 'N/A',
      c4: 'N/A',
    },
    links: {
      guide: 'Common App Essay Guide',
      competency: 'Admissions',
      tools: ['College Shortlist', 'Resume Builder', 'Essay Ideas', 'Resume Optimizer'],
    },
  },
  {
    id: 'best-fit-college',
    unlock: 'academics',
    name: 'Selecting Best Fit College',
    directive:
      'Compare your top college options using outcomes, cost, and opportunity — not prestige alone.',
    rationale:
      'Outcomes depend less on prestige and more on engagement, fit, and opportunities leveraged while enrolled.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Secondary',
      g12: 'Primary',
      c1: 'N/A',
      c2: 'N/A',
      c3: 'N/A',
      c4: 'N/A',
    },
    links: {
      guide: 'College Visit Guide',
      competency: 'Post-Secondary Choices',
      tools: ['Explore Universities'],
    },
  },
  {
    id: 'strong-major',
    unlock: 'academics',
    name: 'Selecting Strong Major',
    directive:
      'Choose a major aligned to your strengths and interests that preserves strong career options.',
    rationale:
      'Majors aligned to strengths, interests, and career paths increase persistence, graduation rates, and early-career earnings.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Secondary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Conditional',
      c3: 'N/A',
      c4: 'N/A',
    },
    links: {
      competency: 'Classes, Majors, Degrees',
      tools: ['Major Decisions', 'Major Calculator'],
    },
  },
  {
    id: 'capstone',
    unlock: 'academics',
    name: 'Capstone / Final Project',
    directive:
      'Design and complete a capstone or final project that demonstrates applied skills and initiative.',
    rationale:
      'Capstone projects demonstrate applied skills, follow-through, and readiness for academic or professional advancement.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Tertiary',
      g12: 'Tertiary',
      c1: 'N/A',
      c2: 'N/A',
      c3: 'Secondary',
      c4: 'Conditional',
    },
    links: { tools: ['Boost Your Profile'] },
  },
  {
    id: 'paying-for-college',
    unlock: 'academics',
    name: 'Paying for College',
    directive:
      'Create a clear, realistic plan to pay for college while minimizing unnecessary debt. Maintain financial awareness throughout all four years of college.',
    rationale:
      'Early financial literacy reduces high-risk debt and increases post-graduation independence and flexibility.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Conditional',
      g12: 'Conditional',
      c1: 'Conditional',
      c2: 'Conditional',
      c3: 'Conditional',
      c4: 'Conditional',
    },
    links: {
      competency: 'Financial Aid',
      tools: ['Lower the Cost of College', 'Explore Universities', 'Find Scholarships'],
    },
  },
  {
    id: 'letters-of-rec',
    unlock: 'academics',
    name: 'Secure Letters of Recommendation',
    directive:
      'Secure strong letters of recommendation by asking the right adults early and setting them up for success.',
    rationale:
      'Strong adult relationships predict resilience, persistence, and access to future opportunities. Adults with subject matter expertise provide alternative, expert opinions and solutions.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Secondary',
      g12: 'Primary',
      c1: 'N/A',
      c2: 'Secondary',
      c3: 'Secondary',
      c4: 'Primary',
    },
    links: { competency: 'Engaging Teachers' },
  },
  {
    id: 'peer-group',
    unlock: 'communities',
    name: 'Peer Group Ties',
    directive:
      'Choose to spend time with peers who challenge you to grow and raise your standards.',
    rationale: 'Peer groups strongly influence effort, risk tolerance, and long-term aspirations.',
    priority: {
      g9: 'Conditional',
      g10: 'Conditional',
      g11: 'Conditional',
      g12: 'Conditional',
      c1: 'Conditional',
      c2: 'Conditional',
      c3: 'Conditional',
      c4: 'Conditional',
    },
    links: { competency: 'Friends/Peer Groups' },
  },
  {
    id: 'strong-ties',
    unlock: 'communities',
    name: 'Strengthen Strong Ties',
    directive:
      'Strengthen relationships with people who consistently support your growth and goals.',
    rationale:
      'Supportive close relationships increase persistence, confidence, and post-secondary success.',
    priority: {
      g9: 'Conditional',
      g10: 'Conditional',
      g11: 'Secondary',
      g12: 'Secondary',
      c1: 'Conditional',
      c2: 'Conditional',
      c3: 'Secondary',
      c4: 'Secondary',
    },
    links: {},
  },
  {
    id: 'weak-ties',
    unlock: 'communities',
    name: 'Strengthen Weak Ties',
    directive:
      'Build relationships with people loosely connected to you and ahead of you academically or professionally.',
    rationale: 'Weak ties are the most common source of new opportunities and career mobility.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Shadowing' },
  },
  {
    id: 'professional-networking',
    unlock: 'communities',
    name: 'Professional Networking',
    directive:
      'Begin building a professional network early and maintain it with intention. This long-term relationship building increases trust, access, and opportunity flow.',
    rationale:
      'Networking skills significantly increase access to internships, jobs, and professional opportunities.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Tertiary',
      g12: 'Tertiary',
      c1: 'Secondary',
      c2: 'Secondary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Networking', tools: ['Jobs You Might Love'] },
  },
  {
    id: 'informational-interviews',
    unlock: 'communities',
    name: 'Informational / Career Interviews',
    directive:
      'Initiate informational or career interviews to understand how careers actually work, test assumptions, build clarity, and make better decisions.',
    rationale: 'Informational interviews reduce costly trial-and-error in career decision-making.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Networking', tools: ['Career Paths'] },
  },
  {
    id: 'sense-of-purpose',
    unlock: 'skills',
    name: 'Stronger Sense of Purpose',
    directive: 'Clarify what you are working toward and articulate why it matters to you.',
    rationale: 'Clear purpose improves motivation, persistence, and decision quality over time.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { tools: ['Blueprint'], other: 'https://youtu.be/WSTcCbhbG7U?si=ZcuAP-acpr04P8ED' },
  },
  {
    id: 'agency',
    unlock: 'skills',
    name: 'Increased Agency',
    directive:
      'Take ownership of your decisions and act without waiting for permission or perfect clarity.',
    rationale:
      'Agency accelerates learning, increases opportunity access, and builds trust with adults and institutions.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Agency' },
  },
  {
    id: 'study-habits',
    unlock: 'skills',
    name: 'Strong Study Habits',
    directive: 'Build and maintain a weekly study system that produces consistent results.',
    rationale:
      'Consistent study systems outperform raw intelligence in predicting academic success.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Secondary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Secondary',
      c4: 'Tertiary',
    },
    links: { guide: 'High School Study Habits Guide', competency: 'Studying' },
  },
  {
    id: 'literacy',
    unlock: 'skills',
    name: 'Literacy Across Disciplines',
    directive:
      'Develop the ability to read, interpret, and engage with complex texts across subjects.',
    rationale:
      'Reading comprehension is a foundational skill that enables success in rigorous coursework, college persistence, and career readiness.',
    priority: { g9: '', g10: '', g11: '', g12: '', c1: '', c2: '', c3: '', c4: '' },
    links: {},
  },
  {
    id: 'time-management',
    unlock: 'skills',
    name: 'Time Management',
    directive: 'Plan your time intentionally so high-impact work actually gets done each week.',
    rationale:
      'Time management protects focus and determines whether high-impact priorities are executed.',
    priority: {
      g9: 'Secondary',
      g10: 'Secondary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Secondary',
      c4: 'Secondary',
    },
    links: { competency: 'Time Management' },
  },
  {
    id: 'ownership-hustle',
    unlock: 'skills',
    name: 'Ownership & Hustle',
    directive: 'Demonstrate reliability by consistently following through on commitments.',
    rationale:
      'Reliability and follow-through are stronger predictors of success than talent alone.',
    priority: {
      g9: 'Tertiary',
      g10: 'Tertiary',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Secondary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Work Ethic' },
  },
  {
    id: 'interview-prep',
    unlock: 'skills',
    name: 'Interview Prep',
    directive:
      'Practice and refine clear, confident stories about your experiences for interviews.',
    rationale:
      'Clear storytelling improves hiring outcomes by demonstrating communication, judgment, and self-awareness.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Interviewing' },
  },
  {
    id: 'get-the-offer',
    unlock: 'skills',
    name: 'Get the Offer',
    directive:
      'Map your target opportunities by competitiveness, interview type (case, behavioral, technical), internal advocates, and mock interview timeline — then put in enough reps to feel sharp.',
    rationale:
      'Offers go to the prepared. Do the homework so you show up confident enough to evaluate them while they evaluate you.',
    priority: { g9: '', g10: '', g11: '', g12: '', c1: '', c2: '', c3: '', c4: '' },
    links: {},
  },
  {
    id: 'growth-mindset',
    unlock: 'skills',
    name: 'Growth Mindset',
    directive: 'Respond to setbacks by extracting lessons and adjusting your approach quickly.',
    rationale:
      'Adaptability and learning from failure predict long-term success in changing environments.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Growth Mindset' },
  },
  {
    id: 'quit-pivot-double-down',
    unlock: 'skills',
    name: 'When to quit, pivot, or double down',
    directive:
      'Decide what to quit, what to pivot, and what to double down on — and act accordingly.',
    rationale:
      'Knowing when to pivot reduces opportunity cost and prevents prolonged misalignment.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Conditional',
      g12: 'Conditional',
      c1: 'Conditional',
      c2: 'Conditional',
      c3: 'Conditional',
      c4: 'Conditional',
    },
    links: {},
  },
  {
    id: 'professionalism',
    unlock: 'skills',
    name: 'Professionalism',
    directive: 'Communicate and show up in ways that consistently build trust and credibility.',
    rationale: 'Professionalism builds trust, credibility, and long-term opportunity access.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'N/A',
      g12: 'Conditional',
      c1: 'Tertiary',
      c2: 'Secondary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { guide: 'Online Reputation Guide', competency: 'Work Place Success' },
  },
  {
    id: 'asking-for-help',
    unlock: 'skills',
    name: 'Asking for help',
    directive: 'Ask for help early, clearly, and strategically — before problems compound.',
    rationale:
      'Asking for help early accelerates learning and prevents small problems from compounding.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { competency: 'Seeking Support' },
  },
  {
    id: 'clarifying-values',
    unlock: 'skills',
    name: 'Clarifying values',
    directive: 'Define your core values and use them to guide major decisions.',
    rationale: 'Clear values improve decision quality under pressure and uncertainty.',
    priority: {
      g9: 'Primary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Primary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { guide: 'Backrs Value Exercise' },
  },
  {
    id: 'evaluating-tradeoffs',
    unlock: 'skills',
    name: 'Evaluating tradeoffs',
    directive: 'Evaluate options using explicit tradeoffs such as time, cost, upside, and risk.',
    rationale: 'Evaluating tradeoffs leads to better long-term outcomes than intuition alone.',
    priority: {
      g9: 'N/A',
      g10: 'Conditional',
      g11: 'Conditional',
      g12: 'Primary',
      c1: 'Secondary',
      c2: 'Secondary',
      c3: 'Secondary',
      c4: 'Primary',
    },
    links: { competency: 'Decision Making' },
  },
  {
    id: 'bias-toward-action',
    unlock: 'skills',
    name: 'Bias toward action',
    directive: 'Take decisive action rather than waiting for certainty or perfect conditions.',
    rationale: 'Bias toward action accelerates learning and reduces missed opportunity cost.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'N/A',
      g12: 'Tertiary',
      c1: 'Tertiary',
      c2: 'Secondary',
      c3: 'Secondary',
      c4: 'Secondary',
    },
    links: {},
  },
  {
    id: 'personal-narrative',
    unlock: 'skills',
    name: 'Personal Narrative & Story Telling',
    directive:
      'Ability to clearly articulate your why, where you are today, and where you want to go. This shows up in elevator pitches, interviews, networking, and everyday conversations.',
    rationale:
      'Students who can articulate their story can connect their experiences to a larger purpose and align their decisions with it. This is not only a useful tool in the interview process, but in general life planning and even relationship building.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Tertiary',
      c2: 'Secondary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: { guide: 'Online Reputation Guide' },
  },
  {
    id: 'ec-involvement',
    unlock: 'achievement',
    name: 'Extra-Curricular Involvement',
    directive:
      'Prioritize a small number of extracurricular activities where you can demonstrate depth and commitment.',
    rationale:
      'Depth of commitment signals focus, reliability, and maturity more clearly than scattered involvement.',
    priority: {
      g9: 'Secondary',
      g10: 'Primary',
      g11: 'Primary',
      g12: 'Primary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Secondary',
    },
    links: {
      guide: 'HS Extra-Curricular Guide',
      competency: 'Extra Curriculars',
      tools: ['Boost Your Profile'],
    },
  },
  {
    id: 'ec-leadership',
    unlock: 'achievement',
    name: 'Extra-Curricular Leadership',
    directive: 'Assume leadership by owning responsibilities with clear and measurable impact.',
    rationale:
      'Leadership experience predicts collaboration skills, career advancement, and long-term professional success.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Secondary',
      g12: 'Primary',
      c1: 'Tertiary',
      c2: 'Secondary',
      c3: 'Secondary',
      c4: 'Secondary',
    },
    links: { competency: 'Extra Curriculars', tools: ['Boost Your Profile'] },
  },
  {
    id: 'community-impact',
    unlock: 'achievement',
    name: 'Community Impact',
    directive:
      'Deliver meaningful community impact and clearly articulate the results of your work.',
    rationale:
      'Meaningful community engagement increases motivation, purpose, and long-term civic and personal well-being.',
    priority: {
      g9: 'N/A',
      g10: 'Secondary',
      g11: 'Secondary',
      g12: 'Tertiary',
      c1: 'Tertiary',
      c2: 'Tertiary',
      c3: 'Tertiary',
      c4: 'Tertiary',
    },
    links: { tools: ['Boost Your Profile'] },
  },
  {
    id: 'formal-employment',
    unlock: 'achievement',
    name: 'Formal Employment',
    directive:
      'Secure and succeed in a job that builds responsibility, reliability, and transferable skills.',
    rationale:
      'Jobs with real responsibility build reliability, confidence, and transferable professional skills.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Secondary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Primary',
    },
    links: {
      competency: 'Full-Time Positions',
      tools: [
        'Career Paths',
        'Resume Builder',
        'Boost Your Profile',
        'Jobs You Might Love',
        'Resume Optimizer',
        'Boost Odds of an Offer',
        'Discover Jobs & Internships',
      ],
    },
  },
  {
    id: 'spike',
    unlock: 'achievement',
    name: 'Spike / Interestingness',
    directive: 'Develop a clear area of depth or spike that differentiates you from peers.',
    rationale:
      'Distinct areas of depth create stronger signals and differentiation in competitive environments.',
    priority: {
      g9: 'N/A',
      g10: 'Secondary',
      g11: 'Secondary',
      g12: 'Secondary',
      c1: 'Secondary',
      c2: 'Secondary',
      c3: 'Secondary',
      c4: 'Secondary',
    },
    links: { tools: ['Boost Your Profile'] },
  },
  {
    id: 'summer-programs',
    unlock: 'achievement',
    name: 'Summer Programs',
    directive:
      'Select and apply to summer programs that directly advance your academic or career goals.',
    rationale:
      'High-quality summer programs accelerate skill development and clarify academic and career direction.',
    priority: {
      g9: 'N/A',
      g10: 'Tertiary',
      g11: 'Tertiary',
      g12: 'Secondary',
      c1: 'Tertiary',
      c2: 'Secondary',
      c3: 'N/A',
      c4: 'N/A',
    },
    links: { tools: ['Essay Ideas', 'Resume Builder', 'Boost Odds of an Offer'] },
  },
  {
    id: 'internship-planning',
    unlock: 'achievement',
    name: 'Start Internship Planning Early',
    directive:
      'Begin exploring industries, building experience, and understanding recruiting timelines as early as freshman year.',
    rationale:
      'Students who gain early internship or professional experience often build momentum faster, strengthen future applications, and increase access to more competitive opportunities later in college.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'N/A',
      g12: 'Conditional',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Conditional',
    },
    links: {},
  },
  {
    id: 'secure-internship',
    unlock: 'achievement',
    name: 'Secure an Internship',
    directive: 'Pursue internships where you can contribute value and build credible experience.',
    rationale:
      'Internships significantly increase the likelihood of securing full-time employment after graduation.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'N/A',
      g12: 'Tertiary',
      c1: 'Secondary',
      c2: 'Primary',
      c3: 'Primary',
      c4: 'Conditional',
    },
    links: {
      guide: 'Internship Guide',
      competency: 'Internships',
      tools: [
        'Career Paths',
        'Resume Builder',
        'Boost Your Profile',
        'Jobs You Might Love',
        'Resume Optimizer',
        'Boost Odds of an Offer',
        'Discover Jobs & Internships',
      ],
    },
  },
  {
    id: 'full-time-job',
    unlock: 'achievement',
    name: 'Securing a Full-Time Job',
    directive:
      'Execute a professional job search, including applications, interviews, and follow-through.',
    rationale:
      'Job search skills compound across careers and materially affect earnings, mobility, and opportunity access.',
    priority: {
      g9: 'N/A',
      g10: 'N/A',
      g11: 'N/A',
      g12: 'Conditional',
      c1: 'Tertiary',
      c2: 'Tertiary',
      c3: 'Secondary',
      c4: 'Primary',
    },
    links: {
      competency: 'Full-Time Positions',
      tools: [
        'Career Paths',
        'Resume Builder',
        'Boost Your Profile',
        'Jobs You Might Love',
        'Resume Optimizer',
        'Boost Odds of an Offer',
        'Discover Jobs & Internships',
      ],
      other: 'https://www.whereyouworkmatters.org/',
    },
  },
];

export const KEY_LEVER_LINK_URLS: Readonly<Record<string, string>> = {
  'High School GPA Guide': '/gpa-guide',
  'High School Study Habits Guide': '/study-habits-guide',
  'College Visit Guide': '/college-visit-guide',
  'Backrs Value Exercise':
    'https://docs.google.com/document/d/19WpJ7CvKoC-SWGsp_wbAj2_QsbQVfnijFxD_3seD2K4/edit?usp=sharing',
  'High School Course Selection': '/course-selection-guide',
  'High School Test Scores Guide': '/standardized-tests-guide',
  'Common App Essay Guide': '/common-app-essay-guide',
  'HS Extra-Curricular Guide': '/extracurriculars-guide',
  'Internship Guide': '/internships-guide',
  'Online Reputation Guide': '/online-reputation-guide',
  Learning: '/competency/foundational-learning',
  'Classes, Majors, Degrees': '/competency/academics-classes-majors-degrees',
  'Test Prep': '/competency/academics-test-prep',
  Admissions: '/competency/academics-admissions',
  'Post-Secondary Choices': '/competency/academics-post-secondary-choices',
  'Financial Aid': '/competency/finances-financial-aid',
  'Engaging Teachers': '/competency/academics-engaging-teachers',
  'Friends/Peer Groups': '/competency/wellness-friends-peer-groups',
  Shadowing: '/competency/careers-shadowing',
  Networking: '/competency/careers-networking',
  Agency: '/competency/mindsets-agency',
  Studying: '/competency/academics-studying',
  'Time Management': '/competency/foundational-time-management',
  'Work Ethic': '/competency/mindsets-work-ethic',
  Interviewing: '/competency/careers-interviewing',
  'Growth Mindset': '/competency/mindsets-growth-mindset',
  'Work Place Success': '/competency/careers-workplace-success',
  'Seeking Support': '/competency/fundamentals-seeking-support',
  'Decision Making': '/competency/foundational-decision-making',
  'Extra Curriculars': '/competency/academics-extracurriculars',
  Internships: '/competency/careers-internships-co-ops',
  'Full-Time Positions': '/competency/careers-full-time-positions',
  'Are You On Track': '/are-you-on-track',
  'Improve Your Test Scores': '/office-hours/improve_your_test_scores',
  'College Shortlist': '/office-hours/college_shortlist',
  'Resume Builder': '/solution-session/resume_builder',
  'Essay Ideas': '/office-hours/essay_ideas',
  'Resume Optimizer': '/office-hours/resume_optimizer',
  'Explore Universities': '/search/universities',
  'Major Decisions': '/office-hours/majors',
  'Major Calculator': '/majors',
  'Lower the Cost of College': '/office-hours/lower_cost_of_college',
  'Find Scholarships': '/search/scholarships',
  'Jobs You Might Love': '/office-hours/jobs_you_might_love',
  'Boost Your Profile': '/office-hours/boost_your_profile',
  'Boost Odds of an Offer': '/office-hours/boost_odds_of_an_offer',
  'Discover Jobs & Internships': '/search/internships',
  Blueprint: '/my-blueprint',
  'Career Paths': '/career-paths',
};
